import { Box, CircularProgress, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { identify } from "../capture";
import { useAuth } from "../context/AuthContext";
import { useWhitelabel } from "../context/WhitelabelContext";
import { hostname, trackAffiliate } from "../utils";
import MfaSetup from "./MfaSetup";
import MfaVerify from "./MfaVerify";

export default function GoogleCallback() {
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, token } = useAuth();
  const { whitelabelSettings, loaded } = useWhitelabel();

  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaNeedsSetup, setMfaNeedsSetup] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [tempUid, setTempUid] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");

    if (!loaded) {
      return;
    }

    // No code to process
    if (!code && !errorParam) {
      return;
    }

    if (errorParam) {
      setError(`Google login failed: ${errorParam}`);
      return;
    }

    if (!code) {
      return;
    }

    console.log("google callback", code, tempToken, tempUid, email, token);
    // Exchange the code for tokens via our backend
    const redirectUri = `${window.location.origin}/auth/google/callback`;
    axios
      .post(`${hostname}/auth/google/callback`, {
        code,
        redirect_uri: redirectUri,
      })
      .then((response) => {
        if (!response.data.success) {
          setError(response.data.error || "Google login failed");
          return;
        }
        const {
          customToken,
          email: userEmail,
          uid,
          displayName,
          photoURL,
          mfaRequired: needsMfa,
          mfaNeedsSetup: needsSetup,
        } = response.data;

        // Check if MFA is required
        if (needsMfa) {
          setTempToken(customToken);
          setTempUid(uid);
          setEmail(userEmail);
          setMfaRequired(true);
          setMfaNeedsSetup(needsSetup || false);
          return;
        }

        // No MFA required, complete sign in
        completeSignIn(customToken, userEmail, uid, displayName, photoURL);
      })
      .catch((err: any) => {
        console.error("Google callback error:", err);
        setError(
          err.response?.data?.error || "Failed to complete Google login",
        );
      });
  }, [loaded]);

  const completeSignIn = async (
    token: string,
    userEmail: string,
    uid: string,
    displayName?: string,
    photoURL?: string,
  ) => {
    console.log("completeSignIn", token, userEmail, uid);
    // Sign in with the token using AuthContext
    signIn(token, { email: userEmail, uid, displayName, photoURL });

    // Track analytics
    identify({ uid, email: userEmail });
    trackAffiliate({ uid, email: userEmail }, whitelabelSettings);

    // Check if user has companies and subscription to determine routing
    try {
      const [companiesResponse, usageResponse] = await Promise.all([
        axios.get(`${hostname}/companies`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${hostname}/usage`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const hasCompanies =
        companiesResponse.data.companies &&
        companiesResponse.data.companies.length > 0;
      const subscriptionType =
        usageResponse.data.usage?.subscriptionType || "free";
      const isFree =
        subscriptionType.toLowerCase() === "free" || subscriptionType === "";

      if (hasCompanies) {
        navigate("/dashboard/companies");
      } else if (
        whitelabelSettings?.disableFreeAccounts &&
        !whitelabelSettings?.customSubscriptionEnabled &&
        isFree
      ) {
        // Only go to /subscribe if free accounts disabled AND user doesn't have a subscription
        navigate("/subscribe");
      } else {
        // Main platform shows the subscription page at the END of onboarding
        // (see Onboarding.tsx handleComplete), so new users go straight to onboarding.
        navigate("/onboarding");
      }
    } catch (checkError) {
      console.error("Error checking companies:", checkError);
      navigate("/dashboard/companies");
    }
  };

  const handleMfaComplete = async (token: string, uid?: string) => {
    completeSignIn(token, email, uid || tempUid);
  };

  const handleMfaBack = () => {
    navigate("/login");
  };

  console.log("Current", mfaRequired, mfaNeedsSetup, tempToken);

  // If MFA is required, show MFA components
  if (mfaRequired) {
    if (mfaNeedsSetup) {
      return (
        <MfaSetup
          tempToken={tempToken}
          email={email}
          uid={tempUid}
          onComplete={(token) => handleMfaComplete(token)}
        />
      );
    } else {
      return (
        <MfaVerify
          tempToken={tempToken}
          email={email}
          uid={tempUid}
          onComplete={handleMfaComplete}
          onBack={handleMfaBack}
        />
      );
    }
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/login")}
        >
          Return to Login
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body1">Completing Google sign in...</Typography>
    </Box>
  );
}
